import { type IGlobalTab, TabType } from '@carboncopyco/types/desktop';
import {
  DemoUrlDisplay,
  SettingMainItemsKeys,
  SettingsSubKeys,
} from '@carboncopyco/types/desktop/appSettings';
import { settingsState } from '../../../shared/appSettings';
import useAsyncMemo from '../../hooks/useAsyncMemo';
import type { IEditorPageInfo } from '../EditorContainer/hooks/useEditorPageInfo';

interface Props {
  tab: IGlobalTab;
  pageInfo?: IEditorPageInfo;
}

const isPresentMode = (tab: IGlobalTab) => {
  return [
    TabType.PRESENT_DEMO,
    TabType.GROUP_DEMO_PRESENTATION,
    TabType.GROUP_TOUR_PRESENTATION,
  ].includes(tab.type);
};

/**
 * Convert the page-not-found url to the original url
 * Example URL: https://dev.outdemo.io/page-not-found?lastSuccessPageUrl=https%3A%2F%2Fexample.com%2Fcloned-page&fromPageUrl=httpss%3A%2F%2Fexample.com%2Fnot-cloned-page
 */
const convertPageNotRecordedUrl = (url?: string): string | undefined => {
  try {
    if (url?.includes('/page-not-found')) {
      const fromPageUrl = new URL(url).searchParams.get('fromPageUrl');
      if (fromPageUrl) {
        return decodeURIComponent(fromPageUrl);
      }
    }
  } catch (e) {
    console.error('Error while converting page-not-found url', e);
  }

  return url;
};

const useGetPageUrl = ({ tab, pageInfo }: Props) => {
  const { data: pageUrl } = useAsyncMemo(async () => {
    const url = convertPageNotRecordedUrl(pageInfo?.url);

    if (!isPresentMode(tab)) return url;

    // For present mode check the AppSettings
    const urlDisplay = await settingsState.getItem(
      SettingMainItemsKeys.PresentationDisplay,
      SettingsSubKeys.DemoUrlDisplay
    );
    if (!pageInfo?.url || urlDisplay === DemoUrlDisplay.hide) return '';
    if (urlDisplay === DemoUrlDisplay.full) return url;
    return url ? new URL(url).hostname : '';
  }, [pageInfo?.url, tab]);

  return pageUrl;
};

export default useGetPageUrl;
